import { Action, createSelector, Store } from '@ngrx/store';
import { getJobsiteSelected } from '@ordersnproducts/modules/order-builder/modules/review/reducers/review.selectors';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { getSelectedBusinessLine, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { LOAD_CONFIG_COMPLETE } from '@ordersnproducts/modules/order-builder/modules/specification/actions/specification.actions';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { NextWorkingDayRequest, NextWorkingDayService } from '@cemex-core/cmx-api';
import { LanguageKey } from '@ordersnproducts/modules/shared/constants/language-key';
import { Features } from '@ordersnproducts/features';
import {
  LoadNextWorkingDayAction,
  LoadNextWorkingDayCompleteAction,
  LoadNextWorkingDayCompleteActionError,
  NextWorkingDayActionTypes,
} from '@ordersnproducts/modules/order-builder/shared/modules/common-specification/actions/next-working-day.actions';
import * as moment from 'moment/moment';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';

const nextWorkingDayFormats = {
  [LanguageKey.English]: 'YYYYMMDD',
  [LanguageKey.Spanish]: 'YYYYMMDD',
};

const getRequestData = createSelector(
  getJobsiteSelected,
  getLegalEntity,
  getSelectedBusinessLine,
  (jobsite, customer, productLine) => {
    return { jobsite, customer, productLine };
  },
);

// These effects use same store as the NextWorkingDayEffect, but these effects are connected to feature
// toggle so they won't dispatch and fill the date picker if the toggle is OFF and also use different API and are extended
// by productTypeCode.
@Injectable()
export class V1OfmNextWorkingDayEffects {
  loadConfig$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CONFIG_COMPLETE),
      filter(() => this.featureToggleService.isFeatureEnabled(Features.NEW_ORDER_AFTER_CUT_OFF)),
      switchMap(() =>
        this.store.select(getRequestData).pipe(
          filter(({ jobsite }) => jobsite !== null),
          take(1),
          map(({ jobsite, customer, productLine }) => {
            const request: NextWorkingDayRequest = {
              countryCode: customer.countryCode,
              shipmentLocationCode: jobsite.shipmentLocationCode,
              languageKey: LanguageKey.Spanish,
              productLineCode: productLine.productTypeCode,
            };
            return new LoadNextWorkingDayAction(request);
          }),
        ),
      ),
    ),
  );

  loadNextWorkingDay$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(NextWorkingDayActionTypes.LOAD),
      switchMap((action: LoadNextWorkingDayAction) =>
        this.nextWorkingDayService.getNextWorkingDayV1(action.payload).pipe(
          map(
            (result) =>
              new LoadNextWorkingDayCompleteAction({
                date: moment(result.dateAvailable, nextWorkingDayFormats[action.payload.languageKey]).toDate(),
              }),
          ),
          catchError(() => of(new LoadNextWorkingDayCompleteActionError())),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<OrderBuilderState>,
    private featureToggleService: FeatureToggleService,
    private nextWorkingDayService: NextWorkingDayService,
  ) {}
}
